import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, ArrowLeft, Loader2, Lock } from "lucide-react";
import { submitLead } from "../../lib/api";

/** Digits only, 10 = FR minimum ; 15 = max E.164. */
const PHONE_MAX_DIGITS = 15;
const PHONE_MIN_DIGITS = 10;
const EMAIL_MAX = 200;

function isValidOptionalEmail(raw) {
  const s = String(raw || "").trim();
  if (!s) return true;
  if (s.length > EMAIL_MAX) return false;
  // Format courant, sans exiger la perfection RFC
  return /^[^\s@]+@[^\s@]+(\.[^\s@]+)+$/.test(s);
}

const QUESTIONS = [
  { id: "employees", label: "Avez-vous au moins 1 salarié ?", hint: "Le dispositif s'adresse à toutes les entreprises employeuses." },
  { id: "urssaf", label: "Êtes-vous à jour de vos cotisations URSSAF ?", hint: "Une simple attestation suffit." },
  { id: "medecine", label: "Êtes-vous inscrit à la médecine du travail ?", hint: "Service interentreprises ou autonome." },
];

const TOTAL_STEPS = QUESTIONS.length + 1;

function FormWrapper({ children, testId }) {
  return (
    <div className="card-surface lift-soft overflow-hidden" data-testid={testId}>
      <div className="tricolor-band" aria-hidden>
        <span />
        <span />
        <span />
      </div>
      <div className="p-7 sm:p-8 min-h-[520px] sm:min-h-[500px] flex flex-col">{children}</div>
    </div>
  );
}

export default function EligibilityForm({ sourceSection = "hero", testIdPrefix = "form-hero" }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ employees: null, urssaf: null, medecine: null });
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const eligible = answers.employees === true && answers.urssaf === true && answers.medecine === true;

  const onAnswer = (q, v) => {
    setAnswers((a) => ({ ...a, [q.id]: v }));
    setTimeout(() => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1)), 220);
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));
  const phoneDigits = phone.replace(/\D/g, "");
  const canSubmit =
    name.trim().length > 1 &&
    phoneDigits.length >= PHONE_MIN_DIGITS &&
    phoneDigits.length <= PHONE_MAX_DIGITS &&
    isValidOptionalEmail(email) &&
    !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await submitLead({
        name: name.trim(),
        phone: phoneDigits,
        email: email.trim() || null,
        company: company.trim() || null,
        employees: !!answers.employees,
        urssaf: !!answers.urssaf,
        medecine: !!answers.medecine,
        source_section: sourceSection,
      });
      setDone({ eligible: res.eligible, message: res.message });
      toast.success(res.eligible ? "Éligible — on vous rappelle sous 24h." : "Demande envoyée.");
    } catch (err) {
      toast.error("Erreur d'envoi. Réessayez.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <FormWrapper testId={`${testIdPrefix}-success`}>
        <div className="w-12 h-12 rounded-full bg-[var(--pc-blue-soft)] flex items-center justify-center">
          <CheckCircle2 className="w-6 h-6 pc-text-blue" strokeWidth={2} />
        </div>
        <h3 className="display-3 pc-text-ink mt-5">
          {done.eligible ? "Vous êtes éligible." : "Demande reçue."}
        </h3>
        <p className="text-[15.5px] pc-text-muted leading-[1.6] mt-3 flex-1">{done.message}</p>
        <div className="mt-auto pt-5 border-t pc-line flex items-center gap-2 text-[12.5px] pc-text-muted">
          <Lock className="w-3.5 h-3.5" strokeWidth={1.8} />
          <span>Données traitées en France · Sans engagement.</span>
        </div>
      </FormWrapper>
    );
  }

  const currentQ = step < QUESTIONS.length ? QUESTIONS[step] : null;
  const progressPct = Math.round((step / (TOTAL_STEPS - 1)) * 100);

  return (
    <FormWrapper testId={`${testIdPrefix}-root`}>
      {/* Eyebrow row — aligned baseline with hero badge */}
      <div className="flex items-center justify-between h-7">
        <div className="flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--pc-blue)] pulse-dot" />
          <span className="text-[10.5px] font-semibold tracking-[0.18em] uppercase pc-text-blue">
            Éligibilité · 30 secondes
          </span>
        </div>
        <span className="text-[11px] pc-text-muted font-mono-num tracking-wider">
          {Math.min(step + 1, TOTAL_STEPS)} / {TOTAL_STEPS}
        </span>
      </div>

      <h3 className="display-3 pc-text-ink mt-2">
        {step < QUESTIONS.length
          ? "Vérifions ensemble."
          : eligible
            ? "Vous êtes éligible."
            : "Étudions votre cas."}
      </h3>

      <div className="mt-5 h-[3px] rounded-full bg-[var(--pc-bg-warm)] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${progressPct}%`, background: "var(--pc-blue)" }}
        />
      </div>

      {/* Q1–3 : hauteur de texte figée (min-h) + pas de mt-auto → Oui/Non à la même hauteur entre toutes les questions */}
      <div className="mt-7 flex-1 flex flex-col min-h-0">
        {currentQ ? (
          <div
            key={currentQ.id}
            className="fade-up flex flex-col shrink-0 w-full"
            data-testid={`${testIdPrefix}-q-${currentQ.id}`}
          >
            <div
              className="flex flex-col min-h-[12rem] sm:min-h-[10.75rem]"
            >
              <div className="text-[11.5px] tracking-[0.16em] uppercase pc-text-muted font-mono-num font-semibold">
                Question 0{step + 1}
              </div>
              <p className="text-[20px] sm:text-[22px] leading-[1.25] pc-text-ink mt-2 font-semibold tracking-[-0.02em]">
                {currentQ.label}
              </p>
              <p className="text-[13.5px] pc-text-muted mt-2 leading-[1.55] grow">{currentQ.hint}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-6">
              {[
                { v: true, label: "Oui" },
                { v: false, label: "Non" },
              ].map((opt) => {
                const active = answers[currentQ.id] === opt.v;
                const isYes = opt.v === true;
                return (
                  <button
                    type="button"
                    key={String(opt.v)}
                    onClick={() => onAnswer(currentQ, opt.v)}
                    data-testid={`${testIdPrefix}-${currentQ.id}-${isYes ? "yes" : "no"}`}
                    className={[
                      "h-13 py-3.5 rounded-xl text-[15.5px] font-medium border transition-all",
                      active
                        ? "bg-[var(--pc-ink)] text-white border-[var(--pc-ink)]"
                        : "bg-white pc-text-ink pc-line-strong hover:border-[var(--pc-ink)] hover:bg-[var(--pc-bg-soft)]",
                    ].join(" ")}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="fade-up flex-1 flex flex-col min-h-0" data-testid={`${testIdPrefix}-contact`}>
            <div className="text-[11.5px] tracking-[0.16em] uppercase pc-text-muted font-mono-num font-semibold">
              Dernière étape
            </div>
            <p className="text-[20px] sm:text-[22px] leading-[1.25] pc-text-ink mt-2 font-semibold tracking-[-0.02em]">
              {eligible
                ? "Laissez votre numéro, on vous rappelle sous 24h."
                : "Laissez vos coordonnées, on étudie votre situation."}
            </p>

            <p className="text-[12px] pc-text-muted leading-snug -mt-1">
              <span className="pc-text-ink font-medium">*</span> champs obligatoires — le rappel se fait par téléphone.
            </p>
            <div className="pt-4 space-y-4">
              <div>
                <label
                  className="block text-[12.5px] font-medium pc-text-ink mb-1.5"
                  htmlFor={`${testIdPrefix}-name`}
                >
                  Nom et prénom <span className="text-rose-600" aria-hidden>*</span>
                </label>
                <input
                  id={`${testIdPrefix}-name`}
                  type="text" placeholder="ex. Jean Dupont"
                  value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full h-12 rounded-xl border pc-line-strong px-4 text-[15.5px] focus:outline-none focus:border-[var(--pc-blue)] focus:ring-2 focus:ring-[var(--pc-blue)]/12 bg-white"
                  data-testid={`${testIdPrefix}-name`} required autoFocus
                />
              </div>
              <div>
                <label
                  className="block text-[12.5px] font-medium pc-text-ink mb-1.5"
                  htmlFor={`${testIdPrefix}-phone`}
                >
                  Téléphone <span className="text-rose-600" aria-hidden>*</span>
                </label>
                <input
                  id={`${testIdPrefix}-phone`}
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="0X XX XX XX XX"
                  value={phone}
                  onChange={(e) => {
                    const d = e.target.value.replace(/\D/g, "").slice(0, PHONE_MAX_DIGITS);
                    setPhone(d);
                  }}
                  maxLength={PHONE_MAX_DIGITS}
                  className="w-full h-12 rounded-xl border pc-line-strong px-4 text-[15.5px] font-mono-num focus:outline-none focus:border-[var(--pc-blue)] focus:ring-2 focus:ring-[var(--pc-blue)]/12 bg-white"
                  data-testid={`${testIdPrefix}-phone`}
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label
                  className="block text-[12.5px] font-medium pc-text-ink mb-1.5"
                  htmlFor={`${testIdPrefix}-email`}
                >
                  E-mail <span className="text-[11px] font-normal pc-text-muted">(facultatif)</span>
                </label>
                <input
                  id={`${testIdPrefix}-email`}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="ex. prenom@entreprise.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.slice(0, EMAIL_MAX))}
                  maxLength={EMAIL_MAX}
                  className="w-full h-12 rounded-xl border pc-line-strong px-4 text-[15.5px] focus:outline-none focus:border-[var(--pc-blue)] focus:ring-2 focus:ring-[var(--pc-blue)]/12 bg-white"
                  data-testid={`${testIdPrefix}-email`}
                />
                {email.trim() && !isValidOptionalEmail(email) ? (
                  <p className="text-[11.5px] text-rose-600 mt-1">Format d’e-mail non reconnu.</p>
                ) : null}
              </div>
              <div>
                <label
                  className="block text-[12.5px] font-medium pc-text-ink mb-1.5"
                  htmlFor={`${testIdPrefix}-company`}
                >
                  Entreprise <span className="text-[11px] font-normal pc-text-muted">(facultatif)</span>
                </label>
                <input
                  id={`${testIdPrefix}-company`}
                  type="text" placeholder="Raison sociale"
                  value={company} onChange={(e) => setCompany(e.target.value)}
                  className="w-full h-12 rounded-xl border pc-line-strong px-4 text-[15.5px] focus:outline-none focus:border-[var(--pc-blue)] focus:ring-2 focus:ring-[var(--pc-blue)]/12 bg-white"
                  data-testid={`${testIdPrefix}-company`}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="btn-primary w-full !py-3.5 mt-3 inline-flex items-center justify-center gap-2"
              data-testid={`${testIdPrefix}-submit`}
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</>
              ) : (
                <>Être rappelé sous 24h <ArrowRight className="w-4 h-4" /></>
              )}
            </button>

            <p className="text-[11.5px] pc-text-muted leading-relaxed pt-3 text-center">
              Aucune carte. Aucun engagement. Données traitées en France.
            </p>
          </form>
        )}
      </div>

      {/* Même hauteur que la ligne « Précédent » dès l’étape 1 : les boutons Oui/Non ne remontent plus au passage à la Q2 */}
      {!done && (
        <div className="mt-3 min-h-9 flex items-center shrink-0" aria-label="Navigation">
          {step > 0 ? (
            <button
              type="button"
              onClick={goBack}
              className="inline-flex items-center gap-1.5 text-[12.5px] pc-text-muted hover:pc-text-ink -ml-0.5"
              data-testid={`${testIdPrefix}-back`}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Précédent
            </button>
          ) : (
            <span className="invisible pointer-events-none text-[12.5px] select-none" aria-hidden>
              <ArrowLeft className="w-3.5 h-3.5 inline" /> Précédent
            </span>
          )}
        </div>
      )}
    </FormWrapper>
  );
}
